// Tenant self-service WhatsApp Business Profile editor (PRD §4.3+).
// Edit the details customers see on your business's WhatsApp chat screen:
// profile photo, About/status, description, address, email, websites, category.
import { useEffect, useState } from 'react';
import { useI18n } from '../lib/i18n';
import { cn } from '../lib/cn';
import { numberLabel, useAccounts } from '../lib/accounts';
import {
  getBusinessProfile, updateBusinessProfile, updateProfilePhoto,
  VERTICALS, type BusinessProfile,
} from '../lib/whatsappProfile';
import {
  whatsappRequestDisplayName,
  whatsappGetDisplayNameStatus,
} from '../lib/whatsappApi';

const FLD = 'w-full rounded-[9px] border border-slate-200 px-3 py-2 text-sm outline-none focus:border-primary dark:border-slate-700 dark:bg-slate-800 dark:text-white';
const EMPTY: BusinessProfile = {
  about: '', address: '', description: '', email: '', vertical: 'UNDEFINED', websites: [], profilePictureUrl: null,
};

export default function WhatsAppProfile() {
  const { t } = useI18n();
  const [p, setP] = useState<BusinessProfile>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [sandbox, setSandbox] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [requestingChange, setRequestingChange] = useState(false);
  const [newDisplayName, setNewDisplayName] = useState('');
  const [displayNameBusy, setDisplayNameBusy] = useState(false);
  const [displayNameErr, setDisplayNameErr] = useState<string | null>(null);
  const [displayNameSuccess, setDisplayNameSuccess] = useState<string | null>(null);
  const [displayNameStatus, setDisplayNameStatus] = useState<{
    verifiedName?: string | null;
    currentDisplayName?: string | null;
    nameStatus?: string | null;
    newDisplayNameStatus?: string | null;
    pendingDisplayName?: string | null;
  } | null>(null);

  // Profile is per number: follow the top-bar account switcher (default when "all").
  const accounts = useAccounts();
  const scopeId = accounts.sendFromId;

  const loadDisplayNameStatus = async (integrationId: string) => {
    try {
      const res = await whatsappGetDisplayNameStatus({ integrationId });
      if (res.ok) {
        setDisplayNameStatus({
          verifiedName: res.verifiedName ?? res.currentDisplayName ?? null,
          currentDisplayName: res.currentDisplayName ?? null,
          nameStatus: res.nameStatus ?? null,
          newDisplayNameStatus: res.newDisplayNameStatus ?? null,
          pendingDisplayName: res.pendingDisplayName ?? null,
        });
      }
    } catch {
      // Retain stored fallback
    }
  };

  useEffect(() => {
    if (accounts.loading) return;
    setLoading(true); setErr(null);
    (async () => {
      try {
        const r = await getBusinessProfile(scopeId);
        setP({ ...EMPTY, ...r.profile });
        setSandbox(r.sandbox);
        if (scopeId) {
          void loadDisplayNameStatus(scopeId);
        }
      } catch (e) {
        setErr((e as Error).message);
      } finally {
        setLoading(false);
      }
    })();
  }, [accounts.loading, scopeId]);

  async function save() {
    setBusy(true); setErr(null); setSaved(false);
    try {
      const r = await updateBusinessProfile({
        about: p.about, address: p.address, description: p.description,
        email: p.email, vertical: p.vertical, websites: p.websites.filter(Boolean),
      }, scopeId);
      setP({ ...EMPTY, ...r.profile }); setSaved(true); window.setTimeout(() => setSaved(false), 1800);
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  }

  async function onPhoto(files: FileList | null) {
    const f = files && files[0]; if (!f) return;
    if (!/image\/(jpeg|png)/.test(f.type)) { setErr('Use a JPEG or PNG image.'); return; }
    setPhotoBusy(true); setErr(null);
    try {
      const dataUrl: string = await new Promise((res, rej) => {
        const rd = new FileReader(); rd.onload = () => res(String(rd.result)); rd.onerror = rej; rd.readAsDataURL(f);
      });
      await updateProfilePhoto(dataUrl, f.type === 'image/png' ? 'image/png' : 'image/jpeg', scopeId);
      setP((x) => ({ ...x, profilePictureUrl: dataUrl }));
    } catch (e) { setErr((e as Error).message); } finally { setPhotoBusy(false); }
  }

  const setWeb = (i: number, v: string) =>
    setP((x) => { const w = [...x.websites]; w[i] = v; return { ...x, websites: w }; });

  if (loading) return <div className="py-16 text-center text-sm text-ink-3">…</div>;

  return (
    <div className="mx-auto w-full max-w-2xl px-4 py-6 sm:px-6">
      <h1 className="text-[22px] font-extrabold text-ink">{t('wap.title')}</h1>
      <p className="mt-1 text-sm text-ink-3">
        {t('wap.sub')}
        {accounts.numbers.length > 1 && accounts.selected && (
          <> · <b className="text-ink">{numberLabel(accounts.selected)}</b></>
        )}
      </p>

      {sandbox && (
        <div className="mt-4 rounded-[9px] border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-700">
          {t('wap.sandbox')}
        </div>
      )}
      {err && <div className="mt-4 rounded-[9px] bg-red-50 px-3 py-2 text-xs font-semibold text-red-600">{err}</div>}

      {/* Photo */}
      <div className="mt-5 flex items-center gap-4 rounded-card border border-slate-200 bg-white p-5 shadow-sm">
        <div className="grid h-20 w-20 flex-shrink-0 place-items-center overflow-hidden rounded-full border border-slate-200 bg-surface text-2xl text-ink-3">
          {p.profilePictureUrl ? <img src={p.profilePictureUrl} alt="" className="h-full w-full object-cover" /> : '👤'}
        </div>
        <div>
          <div className="text-sm font-bold text-ink">{t('wap.photo')}</div>
          <label className="mt-2 inline-block cursor-pointer rounded-[9px] border border-slate-200 px-3 py-2 text-xs font-bold text-ink-2 hover:border-primary">
            {photoBusy ? t('wap.uploading') : t('wap.changePhoto')}
            <input type="file" accept="image/jpeg,image/png" className="hidden" onChange={(e) => void onPhoto(e.currentTarget.files)} />
          </label>
          <p className="mt-1 text-[11px] text-ink-3">JPEG / PNG · square works best · &lt; 5MB</p>
        </div>
      </div>

      {/* Verified Display Name (WP-21 / WP-21b) */}
      {(() => {
        const currentIntegration = accounts.selected as any;
        const verifiedName = displayNameStatus?.verifiedName || displayNameStatus?.currentDisplayName || currentIntegration?.health?.verifiedName || currentIntegration?.verifiedName || currentIntegration?.label || null;
        const nameStatus = displayNameStatus?.newDisplayNameStatus || displayNameStatus?.nameStatus || currentIntegration?.health?.nameStatus || currentIntegration?.nameStatus || currentIntegration?.name_status || null;
        const pendingName = displayNameStatus?.pendingDisplayName || currentIntegration?.pendingDisplayName || null;

        return (
          <div className="mt-4 rounded-card border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-850">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-ink dark:text-white">
                    {t('wap.displayName.title')}
                  </span>
                  {nameStatus && (
                    <span className={cn(
                      'rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider',
                      nameStatus === 'APPROVED' || nameStatus === 'AVAILABLE_WITHOUT_REVIEW'
                        ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300'
                        : nameStatus === 'PENDING_REVIEW'
                        ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300'
                        : nameStatus === 'DECLINED'
                        ? 'bg-rose-100 text-rose-800 dark:bg-rose-950/50 dark:text-rose-300'
                        : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300'
                    )}>
                      {t(`wap.nameStatus.${nameStatus.toLowerCase()}`) || nameStatus}
                    </span>
                  )}
                </div>
                <p className="text-sm font-semibold text-ink-2 dark:text-slate-200">
                  {verifiedName || t('wap.displayName.none')}
                </p>
                <p className="text-[11px] text-ink-3 dark:text-slate-400">
                  {t('wap.displayName.hint')}
                </p>

                {pendingName && (
                  <div className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-300">
                    <span className="font-bold">⏳ {t('wap.displayName.pendingNotice') || 'Under Meta review'}:</span>
                    <span className="font-semibold underline">{pendingName}</span>
                  </div>
                )}

                {displayNameSuccess && (
                  <div className="mt-2 text-xs font-semibold text-emerald-600 dark:text-emerald-400">
                    ✓ {displayNameSuccess}
                  </div>
                )}
              </div>

              {!requestingChange && (
                <button
                  type="button"
                  onClick={() => {
                    setRequestingChange(true);
                    setDisplayNameErr(null);
                    setDisplayNameSuccess(null);
                  }}
                  className="self-start sm:self-auto rounded-[9px] border border-slate-200 px-3 py-1.5 text-xs font-bold text-ink-2 hover:border-primary hover:text-primary dark:border-slate-700 dark:text-slate-300 dark:hover:border-brand-tint"
                >
                  {t('wap.displayName.requestChange')}
                </button>
              )}
            </div>

            {/* Inline Request Form */}
            {requestingChange && (
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  if (!scopeId || !newDisplayName.trim() || displayNameBusy) return;
                  setDisplayNameBusy(true);
                  setDisplayNameErr(null);
                  setDisplayNameSuccess(null);
                  try {
                    const res = await whatsappRequestDisplayName({
                      integrationId: scopeId,
                      newDisplayName: newDisplayName.trim(),
                    });
                    setDisplayNameSuccess(t('wap.displayName.success') || 'Display name change requested successfully');
                    setDisplayNameStatus((prev) => ({
                      ...prev,
                      nameStatus: res.nameStatus || 'PENDING_REVIEW',
                      pendingDisplayName: res.pendingDisplayName || newDisplayName.trim(),
                    }));
                    setRequestingChange(false);
                    setNewDisplayName('');
                    void loadDisplayNameStatus(scopeId);
                    void accounts.refresh();
                  } catch (err: any) {
                    const code = err?.code || '';
                    const msg = err?.message || '';
                    if (
                      code === 'functions/resource-exhausted' ||
                      code === 'resource-exhausted' ||
                      msg.includes('resource-exhausted') ||
                      msg.includes('429')
                    ) {
                      setDisplayNameErr(t('wap.displayName.resourceExhausted') || 'Too many recent name changes — try again later');
                    } else if (
                      code === 'functions/invalid-argument' ||
                      code === 'invalid-argument' ||
                      msg.includes('invalid-argument')
                    ) {
                      const cleanMsg = msg
                        .replace(/^functions\/invalid-argument:\s*/i, '')
                        .replace(/^invalid-argument:\s*/i, '');
                      setDisplayNameErr(cleanMsg || msg);
                    } else {
                      setDisplayNameErr(msg || 'Failed to submit display name request.');
                    }
                  } finally {
                    setDisplayNameBusy(false);
                  }
                }}
                className="mt-4 border-t border-slate-100 pt-4 dark:border-slate-800 space-y-3"
              >
                <div>
                  <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300 mb-1">
                    {t('wap.displayName.newDisplayName')}
                  </label>
                  <input
                    type="text"
                    maxLength={75}
                    value={newDisplayName}
                    onChange={(e) => setNewDisplayName(e.target.value)}
                    placeholder={t('wap.displayName.placeholder')}
                    className={FLD}
                    disabled={displayNameBusy}
                  />
                  <p className="mt-1 text-[11px] text-ink-3 dark:text-slate-400">
                    2–75 chars · No emojis · No trailing punctuation
                  </p>
                </div>

                {displayNameErr && (
                  <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700 dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-300">
                    {displayNameErr}
                  </div>
                )}

                <div className="flex items-center gap-2">
                  <button
                    type="submit"
                    disabled={displayNameBusy || newDisplayName.trim().length < 2}
                    className="rounded-[9px] bg-primary hover:bg-primary-dark px-4 py-2 text-xs font-bold text-white disabled:opacity-50 disabled:cursor-not-allowed transition"
                  >
                    {displayNameBusy ? '…' : t('wap.displayName.submitRequest')}
                  </button>

                  <button
                    type="button"
                    disabled={displayNameBusy}
                    onClick={() => {
                      setRequestingChange(false);
                      setNewDisplayName('');
                      setDisplayNameErr(null);
                    }}
                    className="rounded-[9px] border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                  >
                    {t('common.cancel')}
                  </button>
                </div>
              </form>
            )}
          </div>
        );
      })()}

      {/* Fields */}
      <div className="mt-4 space-y-4 rounded-card border border-slate-200 bg-white p-5 shadow-sm">
        <label className="block">
          <span className="mb-1 block text-xs font-bold text-ink-2">{t('wap.about')}</span>
          <input maxLength={139} value={p.about} onChange={(e) => setP({ ...p, about: e.target.value })} className={FLD} />
          <span className="mt-0.5 block text-[11px] text-ink-3">{t('wap.aboutHint')} · {p.about.length}/139</span>
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-bold text-ink-2">{t('wap.description')}</span>
          <textarea maxLength={512} rows={3} value={p.description} onChange={(e) => setP({ ...p, description: e.target.value })} className={FLD} />
        </label>
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1 block text-xs font-bold text-ink-2">{t('wap.email')}</span>
            <input type="email" value={p.email} onChange={(e) => setP({ ...p, email: e.target.value })} className={FLD} />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-bold text-ink-2">{t('wap.vertical')}</span>
            <select value={p.vertical} onChange={(e) => setP({ ...p, vertical: e.target.value })} className={FLD}>
              {VERTICALS.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
            </select>
          </label>
        </div>
        <label className="block">
          <span className="mb-1 block text-xs font-bold text-ink-2">{t('wap.address')}</span>
          <input value={p.address} onChange={(e) => setP({ ...p, address: e.target.value })} className={FLD} />
        </label>
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1 block text-xs font-bold text-ink-2">{t('wap.website')} 1</span>
            <input value={p.websites[0] ?? ''} onChange={(e) => setWeb(0, e.target.value)} placeholder="https://…" className={FLD} />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-bold text-ink-2">{t('wap.website')} 2</span>
            <input value={p.websites[1] ?? ''} onChange={(e) => setWeb(1, e.target.value)} placeholder="https://…" className={FLD} />
          </label>
        </div>
      </div>

      <div className="mt-4 flex items-center gap-3">
        <button type="button" onClick={() => void save()} disabled={busy}
          className="rounded-[9px] bg-primary px-5 py-2.5 text-[13px] font-bold text-white disabled:opacity-40">
          {busy ? '…' : saved ? t('wap.saved') : t('wap.save')}
        </button>
        {saved && <span className="text-xs font-bold text-green-600">✓ {t('wap.saved')}</span>}
      </div>
    </div>
  );
}
